/**
 * The installable web app's helper (vite.config.ts, Workbox) and its "a new version
 * is ready" prompt (2026-11-01).
 *
 * A new build of Nova never replaces the one open by itself: it is fetched in the
 * background and waits, and this asks. Reload, and the new version opens; Later, and
 * it waits for the next time Nova is opened. An open station window is never reloaded
 * from under the person. It looks for a new build when Nova opens and every hour,
 * since a station keeps the window open for days.
 *
 * Not in the desktop app (its own window; it has its own updates) nor the dev server.
 */

import { useSyncExternalStore } from "react";
import { registerSW } from "virtual:pwa-register";

const CHECK_EVERY_MS = 60 * 60 * 1000;
/** The hand-written helper's cache (before Workbox): nothing reads it any more. */
const OLD_CACHES = ["nova-shell-v1", "nova-shell-v2"];

let waiting = false;
let apply: ((reload?: boolean) => Promise<void>) | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((fn) => fn());

export function startWebApp() {
  if ("caches" in window) OLD_CACHES.forEach((name) => void caches.delete(name).catch(() => undefined));
  apply = registerSW({
    immediate: false,
    onNeedRefresh() {
      waiting = true;
      changed();
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      window.setInterval(() => {
        if (navigator.onLine) void registration.update().catch(() => undefined);
      }, CHECK_EVERY_MS);
    },
  });
}

/** Stop the desktop app's window from ever using one (it was white on Windows: main.tsx). */
export function clearWebApp() {
  void navigator.serviceWorker?.getRegistrations().then((all) => all.forEach((r) => void r.unregister())).catch(() => undefined);
  if ("caches" in window) void caches.keys().then((keys) => keys.forEach((k) => void caches.delete(k))).catch(() => undefined);
}

export function UpdatePrompt() {
  const show = useSyncExternalStore((fn) => {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, () => waiting);
  if (!show) return null;
  return (
    <div className="nv-update-prompt" role="status">
      <span><b>A new version of Nova is ready.</b> Reload to use it.</span>
      <button className="nv-btn primary sm" onClick={() => void apply?.(true)}>Reload</button>
      <button className="nv-btn sm" onClick={() => { waiting = false; changed(); }}>Later</button>
    </div>
  );
}
